import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, Search, Download, Trash2, Eye, Filter, UserX, ArrowLeft, FileText } from 'lucide-react';
import { db } from './firebase';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';

const ViewSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);

  useEffect(() => {
    loadSubscribers();
  }, []);

  useEffect(() => {
    const filtered = subscribers.filter(subscriber =>
      subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSubscribers(filtered);
  }, [subscribers, searchTerm]);

  const loadSubscribers = async () => {
    try {
      const q = query(collection(db, 'newsletter-subscribers'), orderBy('subscribedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const subscribersData = [];
      querySnapshot.forEach((doc) => {
        subscribersData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setSubscribers(subscribersData);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriberId, email) => {
    if (window.confirm(`Are you sure you want to remove ${email} from subscribers?`)) {
      try {
        await deleteDoc(doc(db, 'newsletter-subscribers', subscriberId));
        await loadSubscribers();
        alert('Subscriber removed successfully!');
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        alert('Error removing subscriber. Please try again.');
      }
    }
  };

  const exportToCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Email,Subscribed Date\n"
      + filteredSubscribers.map(subscriber => 
          `${subscriber.email},${new Date(subscriber.subscribedAt?.toDate?.() || subscriber.subscribedAt).toLocaleDateString()}`
        ).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "newsletter_subscribers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <Users className="w-8 h-8 mr-3" />
                  Newsletter Subscribers
                </h1>
                <p className="text-blue-100 mt-2">Manage your newsletter subscriber list</p>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <span className="text-white font-semibold">{subscribers.length} Subscribers</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Controls */}
            <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by email address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={exportToCSV}
                  disabled={filteredSubscribers.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Total Subscribers</p>
                    <p className="text-3xl font-bold text-blue-700">{subscribers.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-600 text-sm font-medium">This Month</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      {subscribers.filter(sub => {
                        const subDate = sub.subscribedAt?.toDate?.() || new Date(sub.subscribedAt);
                        const thisMonth = new Date();
                        thisMonth.setDate(1);
                        return subDate >= thisMonth;
                      }).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">Filtered Results</p>
                    <p className="text-3xl font-bold text-purple-700">{filteredSubscribers.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Filter className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredSubscribers.length} of {subscribers.length} subscribers
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>

            {/* Subscribers List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredSubscribers.length > 0 ? (
              <div className="bg-gray-50 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold text-gray-700">Email Address</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-700">Subscribed Date</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-700">Status</th>
                        <th className="text-center py-4 px-6 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscribers.map((subscriber, index) => (
                        <tr key={subscriber.id} className={`border-b border-gray-100 hover:bg-white transition-colors ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mr-3">
                                <Mail className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{subscriber.email}</p>
                                <p className="text-sm text-gray-500">Subscriber ID: {subscriber.id.slice(0, 8)}...</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="text-gray-700">
                              {formatDate(subscriber.subscribedAt)}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                              Active
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => window.location.href = `mailto:${subscriber.email}`}
                                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                                title="Send Email"
                              >
                                <Mail className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(subscriber.id, subscriber.email)}
                                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                                title="Remove Subscriber"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                <UserX className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-500 mb-2">
                  {searchTerm ? 'No subscribers found' : 'No newsletter subscribers yet'}
                </h3>
                <p className="text-gray-400 mb-4">
                  {searchTerm 
                    ? 'Try adjusting your search criteria' 
                    : 'Newsletter subscribers will appear here when users sign up'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSubscribers;
